import { defineStore } from 'pinia'
import { ref } from 'vue'
import { configService } from '@/services/api'

export const useConfigStore = defineStore('config', () => {
  const campuses = ref([])
  const grades = ref([])
  const subjects = ref([])
  const levels = ref([])
  const paymentMethods = ref([])

  // 新增计价配置数据
  const classTypes = ref([])                    // 班级类型配置
  const levelOptionsByClassType = ref({})       // 按班级类型分组的层次选项
  const subjectDisplayMapping = ref({})         // 科目显示名称映射
  const pricingTiers = ref([])                  // 统一定价表

  // 添加加载状态和错误状态管理
  const loading = ref(false)
  const error = ref(null)
  const lastFetchTime = ref(null)

  const fetchConfigs = async () => {
    // 防止重复请求
    if (loading.value) {
      return
    }

    loading.value = true
    error.value = null

    try {
      const response = await configService.getAll()

      // 安全检查响应数据结构
      if (!response || typeof response !== 'object') {
        throw new Error('API 返回数据格式异常')
      }

      // API 返回结构: {code: 200, message: "...", data: {...}}
      // 需要从 response.data 获取实际配置数据
      const data = response.data || {}

      // 安全设置数据，确保始终为数组
      // 后端 query 函数返回 [rows]，所以需要处理嵌套数组
      const extractData = (d) => {
        if (!d) return []
        return Array.isArray(d) && d.length > 0 && Array.isArray(d[0])
          ? d[0]
          : (Array.isArray(d) ? d : [])
      }

      campuses.value = extractData(data.campuses)
      grades.value = extractData(data.grades)
      subjects.value = extractData(data.subjects)
      levels.value = extractData(data.levels)
      paymentMethods.value = extractData(data.payment_methods)

      // 新增计价配置数据
      classTypes.value = extractData(data.class_types)
      levelOptionsByClassType.value = data.level_options_by_class_type || {}
      subjectDisplayMapping.value = data.subject_display_mapping || {}
      pricingTiers.value = extractData(data.pricing_tiers)

      // 调试日志
      console.log('✅ [ConfigStore] 配置加载完成:')
      console.log('  - classTypes:', classTypes.value.length, '项')
      console.log('  - pricingTiers:', pricingTiers.value.length, '项')
      console.log('  - pricingTiers sample:', JSON.stringify(pricingTiers.value.slice(0, 3)))

      lastFetchTime.value = new Date().toISOString()

    } catch (err) {
      // 记录错误但不抛出，保持应用可用性
      const errorMessage = err.message || '未知错误'
      error.value = errorMessage

      console.error('❌ [ConfigStore] 配置数据加载失败:', errorMessage)

      // 不抛出错误，避免阻塞应用启动
      // 在实际应用中，可以添加用户友好的错误提示
    } finally {
      loading.value = false
    }
  }

  const getCampusById = (id) => {
    return campuses.value.find(campus => campus.id == id)
  }

  const getCampusByName = (name) => {
    return campuses.value.find(campus => campus.name === name)
  }

  const getGradeByName = (name) => {
    return grades.value.find(grade => grade.name === name)
  }

  const getGradeById = (id) => {
    return grades.value.find(grade => grade.id == id)
  }

  const getSubjectById = (id) => {
    return subjects.value.find(subject => subject.id == id)
  }

  const getSubjectByCode = (code) => {
    return subjects.value.find(subject => subject.code === code)
  }

  const getLevelByName = (name) => {
    return levels.value.find(level => level.name === name)
  }

  const getLevelByCode = (code) => {
    return levels.value.find(level => level.code === code)
  }

  const getPaymentMethodByName = (name) => {
    return paymentMethods.value.find(method => method.name === name)
  }

  // 新增计价配置辅助方法

  /**
   * 获取班级类型选项（格式：[{label, value}]）
   */
  const getClassTypeOptions = () => {
    return classTypes.value.map(ct => ({
      label: ct.name,
      value: ct.code
    }))
  }

  /**
   * 获取指定班级类型的层次选项
   */
  const getLevelOptions = (classTypeCode) => {
    return levelOptionsByClassType.value[classTypeCode] || []
  }

  /**
   * 获取科目显示名称
   */
  const getSubjectDisplayName = (subjectCode) => {
    return subjectDisplayMapping.value[subjectCode] || ''
  }

  /**
   * 获取定价信息
   * @param {string} classTypeCode - 班级类型代码
   * @param {number} gradeId - 年级ID
   * @param {string} levelCode - 层次代码
   * @param {string} gradeName - 年级名称（用于匹配定价表）
   * @returns {Object} { price, materialFee }
   */
  const getPricing = (classTypeCode, gradeId, levelCode, gradeName) => {
    console.log('🔍 [getPricing] 查询定价:', { classTypeCode, gradeId, levelCode, gradeName, pricingCount: pricingTiers.value.length })

    // 先尝试使用年级ID匹配（兼容旧数据）
    let pricing = pricingTiers.value.find(p =>
      p.class_type_code === classTypeCode &&
      p.grade_id == gradeId &&
      p.level_code === levelCode
    )
    console.log('  - ID匹配结果:', pricing ? JSON.stringify(pricing) : '未找到')

    // 如果ID匹配不到，尝试使用年级名称匹配
    if (!pricing) {
      // 如果没有传入gradeName，尝试从grades中查找
      let actualGradeName = gradeName
      if (!actualGradeName && gradeId) {
        const grade = getGradeById(gradeId)
        actualGradeName = grade?.name
        console.log('  - 从grades查找年级名称:', actualGradeName)
      }
      
      if (actualGradeName) {
        pricing = pricingTiers.value.find(p =>
          p.class_type_code === classTypeCode &&
          p.grade_name === actualGradeName &&
          p.level_code === levelCode
        )
        console.log('  - 名称匹配结果:', pricing ? JSON.stringify(pricing) : '未找到')
      }
    }

    if (pricing) {
      const result = {
        price: parseFloat(pricing.price) || 0,
        materialFee: parseFloat(pricing.material_fee) || 0
      }
      console.log('  - 返回定价:', result)
      return result
    }

    console.log('  - 未找到定价，返回0')
    return { price: 0, materialFee: 0 }
  }

  /**
   * 获取小班课定价（需要指定层次：QIHANG或YUANHANG）
   */
  const getXiaobanPricing = (gradeId, levelCode) => {
    return getPricing('XIAOBAN', gradeId, levelCode)
  }

  /**
   * 获取VIP定价
   */
  const getVipPricing = (gradeId, levelCode) => {
    return getPricing('VIP', gradeId, levelCode)
  }

  /**
   * 根据班级类型获取所有定价（按年级分组）
   */
  const getPricingByClassType = (classTypeCode) => {
    return pricingTiers.value
      .filter(p => p.class_type_code === classTypeCode)
      .reduce((acc, p) => {
        const gradeId = p.grade_id
        if (!acc[gradeId]) {
          acc[gradeId] = []
        }
        acc[gradeId].push({
          levelCode: p.level_code,
          levelName: p.level_code === null ? 'N/A' : getLevelByCode(p.level_code)?.name || p.level_code,
          materialFee: parseFloat(p.material_fee) || 0,
          price: parseFloat(p.price) || 0
        })
        return acc
      }, {})
  }

  return {
    // 数据
    campuses,
    grades,
    subjects,
    levels,
    paymentMethods,
    classTypes,
    levelOptionsByClassType,
    subjectDisplayMapping,
    pricingTiers,
    // 状态
    loading,
    error,
    lastFetchTime,
    // 方法
    fetchConfigs,
    getCampusById,
    getCampusByName,
    getGradeByName,
    getGradeById,
    getSubjectById,
    getSubjectByCode,
    getLevelByName,
    getLevelByCode,
    getPaymentMethodByName,
    // 新增计价配置方法
    getClassTypeOptions,
    getLevelOptions,
    getSubjectDisplayName,
    getPricing,
    getXiaobanPricing,
    getVipPricing,
    getPricingByClassType
  }
})
